import { Tooltip as TooltipPrimitive } from 'radix-ui';
import { useEffect, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/Collapsible';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { getIntegrationIcon } from '@/integrations/integration-icons';
import { cn } from '@/lib/utils';

import type {
  IntegrationProvider,
  IntegrationStatus,
  ProviderKey,
} from '@littlebird/integrations';

export interface ProviderRailProps {
  providers: readonly IntegrationProvider[];
  selectedKey: ProviderKey | null;
  onSelect(providerKey: ProviderKey): void;
  /** One consolidated stale-data notice; renders as a small pill next to
   * the first group header. Click retries every failed source. */
  refreshIssues?: { message: string; retryAll(): void } | null;
}

const AVAILABILITY_GROUPS: readonly {
  availability: IntegrationProvider['availability'];
  label: string;
}[] = [
  { availability: 'everywhere', label: 'Runs everywhere' },
  { availability: 'this_device', label: 'On this device' },
];

/** Warn pill for failed source refreshes. The icon itself is only the
 * indicator (and spinner while retrying); the action lives on the
 * "Click here to refresh." text inside the hoverable tooltip. Success
 * clears the issues so the pill unmounts; a still-failing refresh lets the
 * timer put the warn state back — there is no completion signal to await
 * from the void retry callbacks. */
function RefreshIssuesPill({
  message,
  retryAll,
}: {
  message: string;
  retryAll(): void;
}) {
  const [retrying, setRetrying] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const handleRefresh = () => {
    setRetrying(true);
    retryAll();
    timerRef.current = setTimeout(() => setRetrying(false), 4000);
  };

  return (
    <TooltipPrimitive.Root delayDuration={200}>
      <TooltipPrimitive.Trigger asChild>
        <span
          tabIndex={0}
          aria-label="Integrations may be out of date"
          className="inline-flex size-4 shrink-0 items-center justify-center rounded-full border border-border-strong text-amber-600 dark:text-amber-400"
        >
          {retrying ? (
            <Icons.loader className="size-2.5 animate-spin text-foreground-muted" />
          ) : (
            <Icons.warn className="size-2.5" />
          )}
        </span>
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side="bottom"
          sideOffset={4}
          className="z-50 max-w-64 rounded-lg border border-border-strong bg-surface px-2 py-1.5 text-xs text-foreground-subtle shadow-sm"
        >
          {message}{' '}
          <button
            type="button"
            disabled={retrying}
            onClick={handleRefresh}
            className="cursor-pointer font-medium text-foreground underline underline-offset-2 disabled:opacity-50"
          >
            Click here to refresh.
          </button>
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export function ProviderRail({
  providers,
  selectedKey,
  onSelect,
  refreshIssues,
}: ProviderRailProps) {
  return (
    <YStack className="w-60 shrink-0 min-h-0 border-r border-border px-3 py-3">
      <YStack className="flex-1 min-h-0 overflow-y-auto gap-4 pt-2">
        {AVAILABILITY_GROUPS.map(({ availability, label }) => {
          const groupProviders = providers.filter(
            (provider) => provider.availability === availability
          );
          if (groupProviders.length === 0) return null;
          return (
            <Collapsible key={availability} defaultOpen>
              <YStack className="gap-1">
                <XStack className="items-center gap-1.5 pr-1.5">
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="group flex min-w-0 flex-1 items-center gap-1.5 px-1.5 py-1 text-left"
                    >
                      <Icons.chevronDown className="size-3 shrink-0 text-foreground-subtle transition-transform group-data-[state=closed]:-rotate-90" />
                      <span className="text-foreground-subtle text-xs">
                        {label}
                      </span>
                    </button>
                  </CollapsibleTrigger>
                  {availability === 'everywhere' && refreshIssues && (
                    <RefreshIssuesPill
                      message={refreshIssues.message}
                      retryAll={refreshIssues.retryAll}
                    />
                  )}
                </XStack>
                <CollapsibleContent>
                  <YStack className="gap-1">
                    {groupProviders.map((provider) => (
                      <ProviderRow
                        key={provider.key}
                        provider={provider}
                        selected={provider.key === selectedKey}
                        onClick={() => onSelect(provider.key)}
                      />
                    ))}
                  </YStack>
                </CollapsibleContent>
              </YStack>
            </Collapsible>
          );
        })}
      </YStack>
    </YStack>
  );
}

function ProviderRow({
  provider,
  selected,
  onClick,
}: {
  provider: IntegrationProvider;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'flex items-center gap-3 w-full text-left px-2.5 py-2 rounded-lg text-sm',
        selected
          ? 'bg-surface-active text-foreground font-medium'
          : 'text-foreground-subtle hover:bg-surface-hover hover:text-foreground'
      )}
    >
      <ProviderIcon provider={provider} />
      <span className="flex-1 truncate">{provider.name}</span>
      <StatusDot status={provider.status} />
    </button>
  );
}

const DOT_COLOR: Record<Exclude<IntegrationStatus, 'usable'>, string> = {
  connecting: 'bg-amber-500',
  needs_attention: 'bg-amber-500',
  offline: 'bg-amber-500',
  disabled: 'bg-foreground-subtle',
};

function StatusDot({ status }: { status: IntegrationStatus }) {
  if (status === 'usable') return null;

  return (
    <span className={cn('size-1.5 rounded-full shrink-0', DOT_COLOR[status])} />
  );
}

export function ProviderIcon({
  provider,
  className = 'size-5 shrink-0',
}: {
  provider: IntegrationProvider;
  className?: string;
}) {
  if (provider.iconUrl) {
    return (
      <img
        src={provider.iconUrl}
        alt=""
        className={cn('rounded object-contain', className)}
      />
    );
  }
  const Icon = getIntegrationIcon(provider.name);
  return <Icon className={className} />;
}
